/**
 * The single source of truth for "what is the effective availability of this
 * service, in this market, right now" — the one resolver every booking surface
 * should consult instead of re-deriving from the individual gating layers.
 *
 * It composes the two RUNTIME availability layers in a fixed precedence:
 *
 *   1. the per-service CMS control plane (`products.marketStatus`, via
 *      `serviceStatusForCountry` in `lib/services-overlay.ts`), and
 *   2. the country/category booking lifecycle (`isWaitlistOnly` /
 *      `isComingSoon` in `i18n/config.ts`, derived from the `*_STATUS` env).
 *
 * Precedence (AHD-7871) — first match wins, so each card resolves to exactly
 * ONE state that drives BOTH its pill and its CTA (no more independent
 * coming-soon pill + not-available CTA on the same card):
 *
 *   hidden        — per-service `hidden`: excluded entirely.
 *   not_available — per-service `not_available`: renders, but no booking/waitlist.
 *   waitlist      — whole market is waitlist-only (`built` / `waitlist`).
 *   coming_soon   — the product CATEGORY isn't open yet (`checkup` market, MRI /
 *                   membership): auto-propagated to every service in that
 *                   category, so editors don't mirror it per service.
 *   coming_soon / waitlist — the per-service CMS status, when the category IS open.
 *   bookable      — available.
 *
 * NOT composed here: the build-time route-existence layer (`*_ENABLED` /
 * `*_PUBLIC`, `page:` gates) and the `section:`/`feature:` registry — those
 * remove whole routes/blocks UPSTREAM, before any card reaches this resolver.
 */

import {
  isComingSoon,
  isWaitlistOnly,
  type ProductCategory,
} from "@/i18n/config"
import { serviceStatusForCountry } from "@/lib/services-overlay"
import type { CmsServiceConfig } from "@/lib/cms"

/** The single effective availability state for a service in a market. */
export type ServiceAvailability =
  | "bookable"
  | "coming_soon"
  | "waitlist"
  | "not_available"
  | "hidden"

export interface ResolveServiceAvailabilityArgs {
  country: string
  /** Booking product family this service belongs to (drives category gating). */
  category: ProductCategory
  /** The card / test `path` (e.g. `/tests/dexa-scan`) or a bare slug. */
  pathOrSlug: string
  /** Flat CMS products control plane (`Array.from(getServicesConfig().values())`). */
  servicesConfig: CmsServiceConfig[]
}

/**
 * Resolve the one effective availability state for a service. See the module
 * doc for the precedence. Pure + synchronous; defaults to `"bookable"` when no
 * layer gates the service (no CMS row, category open).
 */
export function resolveServiceAvailability({
  country,
  category,
  pathOrSlug,
  servicesConfig,
}: ResolveServiceAvailabilityArgs): ServiceAvailability {
  const serviceStatus = serviceStatusForCountry(
    servicesConfig,
    pathOrSlug,
    country
  )

  // 1. Per-service hard exclusions win outright.
  if (serviceStatus === "hidden") return "hidden"
  if (serviceStatus === "not_available") return "not_available"

  // 2. Category lifecycle, auto-propagated to every service in the category.
  if (isWaitlistOnly(country)) return "waitlist"
  if (isComingSoon(country, category)) return "coming_soon"

  // 3. Per-service coming-soon / waitlist (only meaningful once the category is open).
  if (serviceStatus === "coming_soon") return "coming_soon"
  if (serviceStatus === "waitlist") return "waitlist"

  // 4. Available.
  return "bookable"
}

/** Convenience: the service should render its booking CTA as a waitlist trigger. */
export function isWaitlistState(a: ServiceAvailability): boolean {
  return a === "coming_soon" || a === "waitlist"
}
